import { useState } from "react";
import { isPrivateSection, type SpaceSection } from "@al-life/types";
import { api } from "@/lib/api";
import { useSpaceContext } from "./SpaceContext";
import styles from "./spaces.module.css";

/**
 * A thin bar shown above every section page inside a space:
 *  - For MEMBERS: a "Share" control that toggles this section public/private
 *    (`PATCH /spaces/{slug}`) and, when public, shows the copyable link.
 *  - For NON-members viewing a public space: a read-only "example" banner.
 * Rendered once by RequireSpaceAccess, so no feature page needs to know about it.
 */
export function SpaceSectionBar({ section }: { section: SpaceSection }) {
  const { slug, space, canEdit, isPublicSection, reload } = useSpaceContext();
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const isPublic = isPublicSection(section);
  const shareUrl = `${window.location.origin}/${slug}/${section}`;

  async function toggle() {
    if (!space) return;
    setBusy(true);
    const next = isPublic
      ? space.publicSections.filter((s) => s !== section)
      : [...space.publicSections, section];
    const res = await api.spaces.update(slug, { publicSections: next });
    setBusy(false);
    if (res.success) await reload();
    else window.alert(res.error ?? "Could not update sharing.");
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt("Copy this link:", shareUrl);
    }
  }

  // Private-by-design sections (the diary) can never be shared — no toggle.
  if (isPrivateSection(section)) {
    return (
      <div className={styles.bar}>
        <span className={styles.alwaysPrivate}>🔒 Always private</span>
        <span className={styles.shareHint}>
          Only space members can ever see this section — it can't be shared publicly.
        </span>
      </div>
    );
  }

  // Non-member viewing a (public) space → read-only banner, no controls.
  if (!canEdit) {
    return (
      <div className={`${styles.bar} ${styles.barReadOnly}`}>
        <span aria-hidden="true">👀</span>
        <span>
          Viewing <strong>{space?.name ?? slug}</strong>'s space — read-only.
        </span>
      </div>
    );
  }

  return (
    <div className={styles.bar}>
      <button
        type="button"
        className={`${styles.shareBtn} ${isPublic ? styles.shareOn : ""}`}
        onClick={toggle}
        disabled={busy}
        aria-pressed={isPublic}
      >
        {isPublic ? "🌐 Public" : "🔒 Private"}
      </button>
      {isPublic ? (
        <>
          <span className={styles.shareHint}>Anyone with the link can view this section.</span>
          <button type="button" className={styles.copyBtn} onClick={copy}>
            {copied ? "Copied ✓" : "Copy link"}
          </button>
        </>
      ) : (
        <span className={styles.shareHint}>Only you and members can see this section.</span>
      )}
    </div>
  );
}
